import { useMemo } from 'react'

import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import Page from 'app/layout/Page'
import { DataReport } from 'app/lib/components/DataReport'
import { DynamicBanner } from 'app/lib/components/DynamicBanner'
import { useTrackReportView } from 'app/lib/hooks/use-track-report-view'
import { useGetAllOrganizationsQuery } from 'app/redux/scribeApi'

import { OrganizationNamesTable } from './OrganizationNamesTable'

export const ReportsPage: React.FC = () => {
  const { t, i18n } = useTranslation()
  const { data: organizations, isFetching: isFetchingOrgs } = useGetAllOrganizationsQuery()

  const organizationIds = useMemo<string[]>(() => {
    return organizations?.map((org) => String(org.id)) || []
  }, [organizations])

  const organizationNames = useMemo<string[]>(() => {
    const language = i18n.resolvedLanguage as 'en' | 'fr'
    return organizations?.map((org) => org.displayName?.[language] || org.name || '') || []
  }, [organizations, i18n.resolvedLanguage])

  const isReportReady = !isFetchingOrgs && organizationIds.length > 0

  useTrackReportView({
    isReportReady,
    reportType: 'aggregated_organizations',
    organizationIds,
    reportPlatform: 'evidence',
  })

  return (
    <Page isLoading={isFetchingOrgs} scrollable>
      <DynamicBanner pageKey={'reports'} />
      <Typography variant="h1" sx={{ mb: 2 }} data-testid="page-title">
        {t('reportsPage.aggregatedReportTitle')}
      </Typography>
      {organizationNames.length > 0 && (
        <Accordion sx={{ mb: 2 }} data-testid="organization-names">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body1">
              {t('reportsPage.organizationCount', { count: organizationNames.length })}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <OrganizationNamesTable organizationNames={organizationNames} />
          </AccordionDetails>
        </Accordion>
      )}
      <Box sx={{ mt: 1, height: '80vh', pb: 0 }}>
        {isReportReady && (
          <DataReport
            data-testid="aggregatedReports - dataReport"
            organizationIds={organizationIds}
          />
        )}
      </Box>
    </Page>
  )
}
